import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlarmActivityButton } from '@/components/lumen/AlarmActivityDrawer';
import { Icon } from '@/components/lumen/Icon';
import { cn } from '@/lib/utils';
import { NAV_GROUPS } from './nav';
import { stepZoom, useAppearance, useNav } from './theme';
import { applyZoom } from '@/lib/zoom';
import { BrandWordmark } from '@/components/lumen/BrandWordmark';
import { BrandMark } from '@/components/lumen/BrandMark';
import { useDeviceRoles } from '@/lib/api/onboarding';
import { useDocumentFullscreen } from '@/lib/use-fullscreen';
import { wallSites } from '@/modules/overview/noc';
import { useSiteContext } from './site-context-value';
import { hasMainTabs, hasSidebar, onRail } from './layout-logic';
import { useLayoutSettings } from './layout-store';
import { nextClaimId, useMainTabsHost } from './main-tabs-host';
import { useScopePicker } from './scope-picker';
/* The layouts' own CSS, eagerly: the sidebar's edge, the title as picker, a pressable fact and the NOC
   button's display are read by the default layout too. Rules only; the parts load lazily. */
import '@/components/lumen/layout-nav.css';
import { Wallpaper } from '@/components/lumen/Wallpaper';

/* The NOC wall in its own chunk: fetched on the first press, never by a screen that does not open it. */
const NocWall = lazy(() => import('@/modules/overview/NocWall'));

/*
 * THE LAYOUTS' PARTS, LAZY (`npm run bundle:budget`, 2026-11-02: the entry chunk had grown past its
 * 200 KB). Today's sidebar, the default, paints exactly what it painted before the layouts existed:
 * everything a layout setting adds — the header's Search and Apps, Apps and ⌘K, the bottom bar, the
 * dock, the top bar's auto-hide, the layout keys, the gates they read — is `LayoutChrome`, mounted right
 * after the first paint. The main tabs load only where a layout draws them. `layout-entry.test.ts`
 * holds the line.
 */
const MainTabs = lazy(() => import('@/components/lumen/MainTabs').then((m) => ({ default: m.MainTabs })));
const LayoutChrome = lazy(() => import('@/components/lumen/LayoutChrome').then((m) => ({ default: m.LayoutChrome })));

/** The main tabs' row while its part loads: the same line, empty, so the page does not jump. */
function MainTabsSlot(): ReactNode {
  return (
    <Suspense fallback={<nav className="lm-tabs lm-maintabs" aria-hidden />}>
      <MainTabs />
    </Suspense>
  );
}

interface ShellProps {
  tab: string;
  onTab: (id: string) => void;
  /** Unresolved finding count, badged on the Findings item. */
  findingCount?: number;
  /**
   * Nav-item id -> badge count, for anything that is a queue rather than a page.
   *
   * `tone` matters: a pending device is losing data every publish cycle, so it is warn,
   * not neutral. A neutral badge reads as information; this one is a task.
   */
  badges?: Record<string, { count: number; tone: 'warn' | 'danger' } | undefined>;
  /**
   * Nav-item ids to leave OUT of the rail entirely — hidden, not disabled. Used for
   * capability-gated destinations (Remote control): a greyed entry for a feature the
   * organisation has switched off invites "how do I enable this?", and the view behind it
   * refuses politely anyway if reached by URL.
   */
  hiddenItems?: readonly string[];
  /** Rendered in the header, left of the utility buttons. The site selector lives here. */
  headerSlot?: ReactNode;
  /**
   * Rendered LAST in the header — the rightmost control on every screen.
   *
   * A separate slot rather than the tail of `headerSlot` because the position is the point:
   * the account menu is the one control whose place an operator learns once and then reaches
   * for without looking, and it can only be learned if nothing is ever appended after it.
   * Folding it into `headerSlot` puts that guarantee in the caller's hands, where the next
   * person to add a button will quietly take it away.
   */
  accountSlot?: ReactNode;
  /**
   * Open one alarm's own page (`/alarm?id=…`). Wires the header's alarm-timeline drawer
   * (`AlarmActivityButton`), which is otherwise self-contained — everything else it needs
   * (recent activity, the stream's state) it reads straight off `app/alarm-feed.ts`, the
   * same store the bell already reads. This is the one thing only the app shell can supply:
   * which TAB an alarm id opens on.
   */
  onOpenAlarm?: (id: string) => void;
  children: ReactNode;
}

export function Shell({
  tab,
  onTab,
  findingCount,
  badges,
  hiddenItems,
  headerSlot,
  accountSlot,
  onOpenAlarm,
  children,
}: ShellProps): ReactNode {
  /*
   * Fetched here so the device-role catalogue is installed for EVERY screen, not only the
   * three that happen to render a role picker. `model.ts` falls back to its built-in table
   * until this lands, so nothing waits on it — but a site whose roles include a smart lock
   * should not have to visit the device page before the diagram knows what one is.
   */
  useDeviceRoles();

  const fs = useDocumentFullscreen();

  /*
   * THE NOC WALL (Hamzah, 2026-10-20). For an organisation with a data-centre site, the full-screen
   * control opens the wall (`NocWall.tsx`): that site's lamps, alarms and power source, large and dark,
   * each such site in turn. An organisation with none keeps the plain full screen below, unchanged.
   */
  const { selectable, selectedId } = useSiteContext();
  const wallList = useMemo(() => wallSites(selectable, selectedId), [selectable, selectedId]);
  const [wall, setWall] = useState(false);
  const leaveWall = useCallback(() => setWall(false), []);
  const canWall = wallList.length > 0;
  const { toggle: toggleFs } = fs;
  const fill = useCallback(() => {
    if (canWall) setWall((w) => !w);
    else toggleFs();
  }, [canWall, toggleFs]);

  /*
   * SHIFT+F, and deliberately not plain F or F11.
   *
   * F11 belongs to the browser and fighting it would be rude and unreliable. A bare `f`
   * collides with the twin's own single-letter tool keys (R for room, O for opening, V for
   * select), and a shortcut that means one thing on eight screens and another on the ninth
   * is worse than no shortcut.
   *
   * Never while a field has focus: typing "Shift+F" into a site name must produce an F.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'F' && e.key !== 'f') return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName) || t.isContentEditable)) {
        return;
      }
      e.preventDefault();
      fill();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fill]);
  const { collapsed, toggle: toggleNav, open, toggleGroup, seedGroups } = useNav();

  /*
   * THE LAYOUT (prototype v2, Settings › Appearance › Layout; `app/layout-logic.ts`). The rail is drawn
   * here, as it always was; whatever a layout setting adds is `LayoutChrome`, after the first paint.
   */
  const nav = useLayoutSettings((l) => l.nav);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const [painted, setPainted] = useState(false);
  useEffect(() => setPainted(true), []);

  /*
   * THE CONSOLE'S OWN ZOOM (2026-10-26), applied while the shell is mounted and taken off when it
   * unmounts, so the sign-in screens always render at 100 % (`lib/zoom.ts` says how it keeps
   * charts, the map, the SLD and the Twin lined up). The keys are Ctrl/⌘ + Alt with = − 0, the
   * prototype's: the browser keeps its own Ctrl +/− untouched, and a person can tell the two zooms
   * apart by the keys that drive them. Never while a field has focus.
   */
  const zoom = useAppearance((a) => a.zoom);
  const setZoom = useAppearance((a) => a.setZoom);
  /* Before paint, so a person who works at 125 % never sees a frame at 100 % first. */
  useLayoutEffect(() => {
    applyZoom(zoom);
  }, [zoom]);
  useLayoutEffect(() => () => applyZoom(100), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || !e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (/^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName) || t.isContentEditable)) return;
      const cur = useAppearance.getState().zoom;
      if (e.code === 'Equal' || e.code === 'NumpadAdd') setZoom(stepZoom(cur, 1));
      else if (e.code === 'Minus' || e.code === 'NumpadSubtract') setZoom(stepZoom(cur, -1));
      else if (e.code === 'Digit0' || e.code === 'Numpad0') setZoom(100);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setZoom]);

  /*
   * SEED THE ACCORDION FROM WHERE THE OPERATOR ACTUALLY IS.
   *
   * On a first visit only the group containing the current page is open, so the rail shows
   * five headings and the handful of items that are relevant instead of eighteen items at
   * once. `seedGroups` is a no-op once storage holds a choice — an operator who closed
   * everything deliberately keeps it closed.
   */
  const activeGroup = useMemo(
    () => NAV_GROUPS.find((g) => g.items.some((i) => i.id === tab))?.label,
    [tab],
  );
  useEffect(() => {
    if (activeGroup !== undefined) seedGroups([activeGroup]);
  }, [activeGroup, seedGroups]);

  /*
   * THE ACTIVE GROUP IS ALWAYS OPEN, whatever storage says.
   *
   * Navigating into a page whose group the operator had collapsed would otherwise leave the
   * rail with no visible indication of where they are — every heading closed and nothing
   * highlighted. Forcing it open is not overriding their choice; it is the disclosure
   * following the navigation, which is what a drilldown is.
   */
  const isOpen = (label: string): boolean =>
    label === activeGroup || (open?.has(label) ?? false);

  return (
    /*
     * `100dvh`, not `h-screen` (which is `100vh`).
     *
     * `100vh` is the LARGEST the viewport can be — it ignores browser UI that is currently on
     * screen. On Safari, on iOS, and on any window where a toolbar is showing, the shell
     * therefore becomes TALLER than the visible area, the document itself starts scrolling, and
     * scrolling drags the header and the sidebar off the top while leaving dead space below
     * them. That is exactly the reported symptom: the nav "not sticky", with white underneath.
     *
     * `100dvh` tracks the viewport that is actually visible. The `100vh` line before it is the
     * fallback for browsers without dvh, where the old behaviour is still better than nothing.
     * `overflow: hidden` then guarantees the document never scrolls: only `main` does.
     */
    <div
      ref={shellRef}
      className="lm-shell flex flex-col overflow-hidden"
      data-navlayout={nav}
      style={{
        /*
         * FIXED, not just tall. This is what actually stops the document scrolling.
         *
         * `height: 100dvh` + `overflow: hidden` on html/body was not enough. Highcharts'
         * accessibility module appends absolutely-positioned announcer and keyboard-proxy
         * containers, and on a 900 px viewport they landed at bottom 1007 — so the document
         * gained 107 px of scroll while every box we own measured exactly 900. Verified: a
         * wheel over the header moved `window.scrollY` to 107 and put the header at top
         * `-107 px`, sliding the header and the rail off the screen. That is the whole of
         * "extra scroll on the overview page" and "the left menu is not sticky".
         *
         * `position: fixed; inset: 0` takes the entire shell out of document flow, so no
         * descendant — ours or a library's — can extend the page. Only `main` scrolls, which
         * is the contract this layout has always claimed.
         *
         * Deliberately NOT solved by hunting Highcharts' injected nodes: those proxy
         * containers hold the focusable elements that make charts keyboard-navigable, so
         * pinning or shrinking them would trade a scrollbar for an accessibility regression.
         */
        position: 'fixed',
        inset: 0,
      }}
    >
      {/*
        * BYPASS BLOCKS (WCAG 2.4.1, Level A) — the first thing the keyboard reaches.
        *
        * Measured before this existed: fourteen Tab presses from the top of Overview and
        * focus was still outside `main`, because the header's five controls come first and
        * then every rail item in every expanded group. On a console with nine modules that
        * is roughly twenty-five stops to reach the content — on EVERY page, every time.
        *
        * Hidden until focused rather than always visible: sighted mouse users never need
        * it, and the design is deliberately dense. It has to be the first element in DOM
        * order to be the first tab stop, which is why it sits above the header rather than
        * inside it.
        */}
      <a href="#lm-main" className="lm-skip">
        Skip to content
      </a>

      {/* THE WALLPAPER (prototype v2, Settings › Appearance › Wallpaper; `components/lumen/Wallpaper.tsx`):
          the back-most layer, a fixed `.lm-bd` at z-index -1 inside the shell, which Glass isolates, so
          it sits under everything the shell draws and above nothing outside it. Lazy, and nothing at
          all for Solid, Charcoal or the console glow, so today's look is untouched. */}
      <Wallpaper />

      {/* THE GLASS LOOK'S GLOW (`glass.css`): the approved mock's wash, pool and three wisps, under
          everything the shell draws. `display: none` in Solid, so the default look is untouched. */}
      <div className="lm-glow" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      {/* ------------------------------------------------------- header */}
      {/*
        * `lm-glass` — the fixed chrome. Solid in the default look; in the person's Glass look
        * (`glass.css`) it blurs what is behind it. The header is fixed, so its backdrop never
        * moves and the blur is composited once rather than re-sampled per scroll frame. See
        * `glass.css` for why a `.lm-card` is see-through but deliberately not blurred.
        */}
      <header
        ref={headerRef}
        className="lm-glass lm-topbar flex shrink-0 items-center gap-[10px] border-b px-4"
        style={{ height: 'var(--header-h)' }}
      >
        {/*
          * Two-line brand, as the reference design has it. The tagline is not decoration: this
          * console sits alongside a BMS, a DCIM and an NMS on the same operator's screen, and
          * "Thunder" alone does not say which one it is. It is hidden below 640px, where the site
          * selector matters more than the product's own name.
          */}
        {/*
          * THE WORDMARK CARRIES THE NAME, so the adjacent text no longer repeats it.
          *
          * The artwork already renders "Thunder"; typesetting it again beside the mark would
          * say the name twice in 200px.
          *
          * NO TAGLINE. There was a "Power Standards Compliance" line here, and it described
          * a narrower product than this one: Thunder is a remote monitoring and control
          * platform that ALSO does standards and compliance monitoring. A subtitle that names
          * only the compliance half misrepresents it to the operator reading the header.
          *
          * ALL FOUR THEME STATES, via `BrandWordmark` rather than `<picture media>`.
          *
          * This was a `<picture>` with `media="(prefers-color-scheme: dark)"` and a single
          * `<img>`, which reads the OS preference and CANNOT see `data-theme` — the attribute
          * this console's own theme toggle writes. Measured on the real header, the two states
          * where the pair disagrees rendered the logo INVISIBLE: OS-light with the dark theme
          * picked the dark ink on --card #161618 (1.01:1), and OS-dark with the light theme
          * picked the light ink on #ffffff (1.0:1). Clicking the header's own theme button on a
          * default OS-light machine was enough to reproduce it.
          *
          * `BrandWordmark` ships both cuts and hides one in CSS, keyed on `data-ink` AND
          * `data-theme` AND `prefers-color-scheme` — so it is correct in all four combinations
          * including the system default, which sets no `data-theme` at all.
          */}
        {/*
          * ON A PHONE, THE MARK ALONE (under 640 px, 2026-11-02). The wordmark is 82 px at this height,
          * and at 390 px the bar's own controls, the crumb and the account menu need that width: the
          * account menu was pushed off the screen. The mark (`BrandMark`, a self-contained tile, the
          * same in every theme) still says whose console it is, in 24.
          */}
        {/* THE BRAND GOES HOME (Hamzah, 2026-11-03: "why clicking on icon is not navigating to the
            overview"). A real link, so a middle or ⌘-click still opens Overview in a new tab; a plain
            click goes the rail's own way. */}
        <a
          href="/overview"
          aria-label="Thunder — Overview"
          className="flex flex-none items-center rounded-[6px]"
          onClick={(e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            onTab('overview');
          }}
        >
          <span className="hidden items-center gap-[10px] sm:flex">
            <BrandWordmark height={22} />
          </span>
          <span className="flex items-center sm:hidden">
            <BrandMark size={24} />
          </span>
        </a>
        {/* The page's name is in the crumb (`HeaderScope`, prototype v2), not beside the brand: it
            sat here as "THUNDER │ Overview" for a day (2026-10-08) and came out again. */}

        {/* The tools take the bar's width so the crumb (`HeaderScope`, first in `headerSlot`) sits at the
            left beside the wordmark and pushes everything after it to the right (`.lm-scope`'s
            `margin-right: auto`, app.css) — in every look since prototype v2 (2026-11-02). */}
        <div className="lm-topbar-tools flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
          {headerSlot}
          {/* THE SEARCH PILL's place (prototype v2): `LayoutChrome` draws it here in every layout but
              today's sidebar. Empty and box-less (`display: contents`) until then. */}
          <span className="contents" data-slot="lm-search" />
          {/*
            * FULL SCREEN FOR THE WHOLE PRODUCT, and the header is the only honest place for
            * it: it is the one control that is present on every screen, and full screen is a
            * property of the window rather than of whatever view happens to be open.
            *
            * Hidden entirely when the browser does not support it — an operator pressing a
            * button that cannot work has no way to tell that from a bug. The Digital Twin
            * keeps its own stage-only control; the two are different elements, so this one
            * reports the WINDOW's state and stays correct while the stage is expanded.
            *
            * With a data-centre site in the organisation it opens the NOC wall instead (2026-10-20),
            * which is the kiosk mode the hook's own docblock calls a different feature: it is, and
            * its way back is Escape and its own Leave button. The wall still opens where the browser
            * refuses full screen, so the control is offered there too.
            */}
          {(fs.supported || canWall) && (
            <button
              type="button"
              onClick={fill}
              /* At every width in the sidebar layout. Under 640 px a launcher layout gives its 32 px to
                 the crumb and the account menu and offers the same act in Apps and ⌘K (`layout-nav.css`,
                 `.lm-noc-btn`): reachable everywhere, never removed (rule 1). */
              className="lm-noc-btn lm-hov items-center justify-center rounded-[var(--radius-sm)]"
              style={{
                width: 'var(--header-ctl-h)',
                height: 'var(--header-ctl-h)',
                color: fs.isFullscreen || wall ? 'var(--fg)' : 'var(--muted-fg)',
              }}
              aria-pressed={canWall ? wall : fs.isFullscreen}
              aria-label={canWall ? 'NOC wall' : fs.isFullscreen ? 'Leave full screen' : 'Fill the screen'}
              title={
                canWall
                  ? 'The NOC wall: each data-centre site’s lamps, alarms and power source, full screen and dark. Escape leaves (Shift+F)'
                  : fs.isFullscreen
                    ? 'Leave full screen — Escape also works (Shift+F)'
                    : 'Fill the screen, hiding the browser’s own furniture (Shift+F)'
              }
            >
              <Icon name={fs.isFullscreen ? 'ic-minimize' : 'ic-maximize'} size={16} />
            </button>
          )}
          {/*
            * ALARM ACTIVITY, REACHABLE FROM EVERY SCREEN — an operator deep in Telemetry or
            * the Twin should not have to leave the screen they are reading to see what just
            * fired. Intrinsic to the shell like full screen, rather than
            * threaded through `headerSlot`, so it cannot be dropped by a future screen that
            * rebuilds that slot. `onOpenAlarm` defaults to a no-op only for a caller that has
            * not wired it yet; the one real caller (`App.tsx`) always does.
            */}
          <AlarmActivityButton onOpenAlarm={onOpenAlarm ?? (() => {})} />
          {/* The zoom, said in the bar only while it is not 100 %: a console drawn at 150 % should
              say so where the eye already is, and one press puts it back. At 100 % nothing is
              drawn, so the bar is exactly what it was. The steps live in Profile › Appearance. */}
          {zoom !== 100 && (
            <button
              type="button"
              onClick={() => setZoom(100)}
              className="lm-hov cl-num rounded-[var(--radius-sm)] border border-[var(--border)] px-[8px] text-[12px] font-semibold text-[var(--muted-fg)]"
              style={{ height: 'var(--header-ctl-h)' }}
              aria-label={`Zoom ${zoom}%. Reset to 100%`}
              title="The console's zoom. Press to go back to 100% (Ctrl+Alt+0)"
            >
              {zoom}%
            </button>
          )}
          {/*
            * NO APPEARANCE MENU IN THE BAR (prototype v2, 2026-11-02; Asif's review, point 5: "move
            * theme controls out of the main header into the Appearance page"). Light or dark, the
            * look, the glass and glow dials and the zoom are the person's and live in Profile ›
            * Appearance, reached from the account menu's Appearance row; the organisation's palette
            * and accent are stated there, as the menu stated them. The menu (`ThemeMenu`) is gone.
            */}

          {/*
            * THE ACCOUNT MENU IS LAST, and therefore rightmost on every screen.
            *
            * It used to sit inside `headerSlot`, ahead of full screen and the theme toggle,
            * on the reasoning that a notification badge next to "sign out" is a misclick
            * waiting to happen. The owner asked for it at the end instead, and the trade is
            * a fair one: the corner is where every other console puts an account, so it is
            * the first place an operator looks, and the two controls now between it and the
            * bell are the two least urgent things in the bar.
            *
            * The misclick worry is answered where it belongs — sign out sits at the BOTTOM
            * of the opened menu, not under the trigger.
            */}
          {/* APPS's place (prototype v2): every screen by group, drawn here by `LayoutChrome` when a
              layout has no rail. Under 900 px the bottom bar carries it instead. */}
          <span className="contents" data-slot="lm-apps" />
          {accountSlot}
        </div>
      </header>

      {/*
        * A REFUSAL IS SAID OUT LOUD. `requestFullscreen` rejects when the call is not
        * user-activated, when the document is inside an iframe without
        * `allow="fullscreen"`, or under a kiosk policy — and every one of those produces a
        * button that appears broken. The operator deserves to know it was the browser's
        * decision and not ours.
        */}
      {/* Portalled to <body>, beside every drawer and toast: in the person's Glass look the shell
          is isolated (`glass.css`) and nothing inside it can rise above it, and the wall must
          cover the whole console in either look. Its own `z-index: 200` decides the rest, as before. */}
      {wall &&
        canWall &&
        createPortal(
          <Suspense fallback={null}>
            <NocWall sites={wallList} onLeave={leaveWall} />
          </Suspense>,
          document.body,
        )}
      {fs.error !== null && (
        <div
          role="alert"
          className="px-[var(--page-pad-x)] py-[7px] text-[11.5px]"
          style={{
            background: 'rgb(var(--warn-rgb) / 12%)',
            borderBottom: '1px solid var(--border)',
            color: 'var(--fg)',
          }}
        >
          {fs.error}
        </div>
      )}

      <div className="lm-shell-body flex min-h-0 flex-1">
        {/* ----------------------------------------------------- sidebar */}
        {/*
          * `lm-rail` carries what used to be inline here — sticky, pinned to the top, full height
          * (app.css says why) — so the person's Glass look can float the rail off the edge
          * (`glass.css`) without fighting an inline style. Only the width stays inline: it is
          * runtime state.
          */}
        {hasSidebar(nav) && (
        <aside
          className="lm-glass lm-rail flex shrink-0 flex-col border-r"
          style={{
            width: collapsed
              ? 'var(--sidebar-w-collapsed)'
              : 'var(--sidebar-w)',
            transition: 'width 180ms var(--ease-press)',
          }}
        >
          <nav className="flex flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden py-2">
            {NAV_GROUPS.map((group, gi) => {
              /* Capability-hidden entries leave the rail AND the rolled-up badge sums —
                 a count for an item that cannot be opened is a task with no door. In Top bar +
                 Sidebar the screens the main tabs carry leave it too (`layout-logic.ts`'s `onRail`). */
              const items = group.items.filter((i) => !hiddenItems?.includes(i.id) && onRail(nav, i.id));
              if (items.length === 0) return null;
              /*
               * THE GROUP'S ROLLED-UP BADGE COUNT, and it is the reason this accordion is
               * safe to ship.
               *
               * A badge in this rail is not decoration — a pending device is discarding
               * measurements every publish cycle, and an unassigned feed is discarding all
               * of them. Hiding items behind a closed heading would hide those counts, so
               * an operator could sit on a page with four gateways silently dropping data
               * and see nothing. The heading therefore carries the SUM of its children's
               * badges whenever it is closed, with the worst tone winning: a danger badge
               * inside a group makes the group's badge danger, not warn.
               */
              const counts = items.map((item) => {
                const extra = badges?.[item.id];
                const n =
                  item.id === 'findings' && findingCount
                    ? findingCount
                    : (extra?.count ?? 0);
                return { n, tone: extra?.tone ?? 'danger' };
              });
              const groupTotal = counts.reduce((a, c) => a + c.n, 0);
              const groupTone = counts.some((c) => c.n > 0 && c.tone === 'danger')
                ? 'danger'
                : 'warn';
              const opened = isOpen(group.label);

              return (
              <div key={group.label} className="flex flex-col gap-px">
                {collapsed ? (
                  /*
                   * COLLAPSED RAIL HAS NO ACCORDION. At 66px there is no room for a
                   * heading, and hiding icons behind an invisible disclosure would leave
                   * the operator with a rail they cannot navigate. Icons only, divided.
                   */
                  gi > 0 && (
                    <div
                      className="mx-[14px] my-2 h-px"
                      style={{ background: 'var(--border)' }}
                    />
                  )
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                    aria-expanded={opened}
                    className="lm-hov flex w-full items-center gap-[6px] px-[15px] pb-[7px] text-left text-[12px] font-semibold"
                    style={{
                      color: opened ? 'var(--fg)' : 'var(--muted-fg)',
                      paddingTop: gi > 0 ? 16 : 4,
                      transition: 'color var(--dur-colour)',
                    }}
                    title={
                      group.label === activeGroup
                        ? `${group.label} — contains the page you are on, so it stays open`
                        : opened
                          ? `Collapse ${group.label}`
                          : `Expand ${group.label}`
                    }
                  >
                    {/* The chevron is the affordance, so it keeps its own colour even when
                        the label is quiet. `Icon` paints `currentColor`, so the wrapper is
                        how a colour is passed to it. */}
                    <span style={{ color: 'var(--muted-fg)', display: 'flex' }}>
                      <Icon name={opened ? 'ic-chevdown' : 'ic-chevright'} size={12} />
                    </span>
                    <span className="truncate">{group.label}</span>
                    {!opened && groupTotal > 0 && (
                      <span
                        className="lm-num ml-auto rounded-[var(--radius-pill)] px-[6px] py-px text-[10px] font-bold"
                        style={{
                          background: `rgb(var(--${groupTone}-rgb) / 16%)`,
                          color: `var(--${groupTone})`,
                        }}
                        title={`${groupTotal} item(s) need attention inside ${group.label}`}
                      >
                        {groupTotal > 99 ? '99+' : groupTotal}
                      </span>
                    )}
                  </button>
                )}

                {(collapsed || opened) &&
                  items.map((item) => {
                  const active = tab === item.id;
                  const extra = badges?.[item.id];
                  const rawCount =
                    item.id === 'findings' && findingCount
                      ? findingCount
                      : (extra?.count ?? 0);
                  const badge = rawCount > 0 ? (rawCount > 99 ? '99+' : String(rawCount)) : null;
                  const badgeRgb =
                    extra?.tone === 'warn' ? 'var(--warn-rgb)' : 'var(--danger-rgb)';
                  const badgeFg = extra?.tone === 'warn' ? 'var(--warn)' : 'var(--danger)';

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onTab(item.id)}
                      title={item.label}
                      aria-current={active ? 'page' : undefined}
                      /* A RAIL, NOT A PILL — the brand edge, a brand-tinted fade and the brand
                         glyph mark where you are (`.lm-rail-item` in app.css, keyed on
                         `aria-current`). The Glass look outlines the item instead (`glass.css`). */
                      className={cn(
                        'lm-rail-item flex h-10 w-full items-center gap-[10px] text-[13.5px]',
                        collapsed ? 'justify-center px-0' : 'justify-start px-[15px]',
                      )}
                    >
                      {/* The active item's glyph goes brand-green — the edge, the fill and
                          the icon all say the same thing, which is what makes position
                          readable at a glance in a 66px rail as well as a 238px one. */}
                      <span className="lm-rail-ico flex">
                        <Icon name={item.icon} size={17} />
                      </span>
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.label}</span>
                          {badge && (
                            <span
                              className="lm-num ml-auto rounded-[var(--radius-pill)] px-[7px] py-px text-[10.5px] font-bold"
                              style={{
                                background: `rgb(${badgeRgb} / 14%)`,
                                color: badgeFg,
                              }}
                            >
                              {badge}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={toggleNav}
            className={cn(
              'lm-hov flex h-[46px] items-center gap-[10px] border-0 border-t text-[12.5px] font-semibold',
              collapsed ? 'justify-center px-0' : 'justify-start px-[17px]',
            )}
            style={{ borderColor: 'var(--border)', color: 'var(--muted-fg)' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={collapsed ? 'ic-chevright' : 'ic-chevleft'} size={17} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </aside>
        )}

        {/* -------------------------------------------------------- view */}
        {/*
          * TRANSPARENT, so `body`'s fixed gradient shows through.
          *
          * It painted `--bg` flat, which put an opaque sheet over the ground and left every
          * translucent panel with nothing to be translucent against — the glass read as a
          * slightly lighter grey box. The gradient is on `body` and fixed precisely so it
          * does not move while this scrolls.
          */}
        <main
          id="lm-main"
          /* -1 so the skip link can move focus HERE. Without it the browser scrolls to the
             element but leaves focus on the link, so the next Tab returns to the rail —
             the jump appears to work and does nothing for the keyboard. */
          tabIndex={-1}
          className="min-w-0 flex-1 overflow-y-auto"
          style={{ background: 'transparent' }}
        >
          {/* The main tabs on a screen that draws its own head (no `Page`), drawn here by `LayoutChrome`
              in the layouts that have them (`app/main-tabs-host.ts`). */}
          <div className="contents" data-slot="lm-maintabs" />
          {children}
        </main>
      </div>
      {/* Everything a layout setting adds, after the first paint (see the top of this file). */}
      {painted && (
        <Suspense fallback={null}>
          <LayoutChrome
            shellRef={shellRef}
            headerRef={headerRef}
            tab={tab}
            onTab={onTab}
            hiddenItems={hiddenItems}
            badges={badges}
            findingCount={findingCount}
            fill={fill}
            canWall={canWall}
            fsSupported={fs.supported}
            isFullscreen={fs.isFullscreen}
            toggleNav={toggleNav}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * One fact in the page head — a label and its value as words since prototype v2 (2026-11-02).
 *
 * `hint` is the second line — a unit, a qualifier, the thing that stops a bare number being
 * ambiguous. `tone` paints the VALUE only, never the label: a red label reads as "this
 * measurement is broken" where a red value reads as "this measurement is bad".
 */
export interface PageStat {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'ok' | 'warn' | 'danger';
  /** Hover text. Say what the number counts, not what it is called again. */
  title?: string;
  /**
   * EVERY FIGURE OPENS SOMETHING (Hamzah, 2026-11-02: "everything is clickable"). With `onPress` the fact
   * is a quiet button that opens its filtered screen or quick view: the same words and look, an underline
   * on hover, a focus ring, Enter and Space. Without it, plain words as before.
   */
  onPress?: () => void;
}

/**
 * Page container.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HEADER IS ONE ROW, AND IT USED TO BE THREE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It was a 19px title, a 13.5px description on its own line beneath it, and `actions`
 * floated right — about 76px of chrome before any content, of which the right two-thirds
 * was empty on every page that passed no actions. Measured on the Overview screenshot: the
 * title row ended at x=420 on a 1512px viewport.
 *
 * Now: title, description INLINE after it, and a `stats` strip that fills the space to the
 * right. That reclaims roughly 30px of vertical on every screen in the product and puts
 * the numbers an operator came for on the first line rather than in a card below the fold.
 *
 * The description stays rather than becoming a tooltip. These pages answer questions an
 * operator does not necessarily know they are asking — "reconstructed from alarm intervals,
 * 30-minute buckets" is the difference between reading a chart correctly and misreading it
 * — and a caption nobody hovers is a caption nobody reads. It wraps below the title on a
 * narrow viewport, where the inline form would squeeze the title to two words.
 */

export function Page({
  title,
  desc,
  actions,
  stats,
  fill,
  stickyHeader,
  titleInBar,
  lead,
  band,
  views,
  scopeTitle,
  children,
}: {
  title: string;
  /**
   * THE PAGE'S OWN VIEWS, as the quiet segment at the right of its tab row (prototype v2's PageBar:
   * "the page's own views as a quiet segment on the right"). In the Top bar layout that row is the
   * main screens' tabs; in the sidebar layout it sits at the right of the page head. A `Segmented`,
   * by convention: underline goes to another screen, a segment changes the view of this one.
   */
  views?: ReactNode;
  /**
   * THE TITLE IS THE SCOPE PICKER (prototype v2: "Jamshoro ⌄", "K-Solar fleet ⌄"): pressing it opens
   * the header's own organisation / site panel under the title (`app/scope-picker.ts`), never a second
   * picker. Plain text when there is nothing to pick, as the header's crumb is.
   */
  scopeTitle?: boolean;
  /**
   * NO VISIBLE PAGE NAME (Hamzah, 2026-10-08: "remove the heading from here", then the same day
   * "remove on all of them from the top nav bar … not from the sidebar"). On a screen whose
   * heading only repeated its rail entry and the site already in the header ("Overview
   * Jamshoro"), the page's first row is given to `lead` — the verdict — instead. The name went
   * into the app bar for a day and came out of it again: the rail's highlight already says where
   * you are. The `<h1>` stays, visually hidden, so the page still has its heading for a screen
   * reader and a test. `desc` is not drawn: it was the scope name the header's picker shows.
   */
  titleInBar?: boolean;
  /** What leads the header row where the title was — the verdict, on a `titleInBar` page. */
  lead?: ReactNode;
  /**
   * A line of its own at the foot of the header, inside it: pinned with it on a `stickyHeader` page
   * and outside the scroller on a `fill` one, so it stays on screen. The site status strip
   * (`SiteStatusStrip`, 2026-10-20) is the one caller: a data-centre site's lamps, always in view.
   */
  band?: ReactNode;
  /**
   * The caption beside the title. A plain string on almost every screen.
   *
   * WIDENED FROM `string` so a page can put FIGURES in its own caption rather than in a band
   * beneath the header. The site hub is the case that needed it: its capacity, load and
   * commissioning year used to render in a bordered strip under the sub-nav, which cost a
   * whole horizontal band to say what the header was already the place for — and read as a
   * fourth row of chrome above content that starts below the fold.
   *
   * INLINE ELEMENTS ONLY. This renders inside a `<p>`, so a `<div>` here is invalid HTML and
   * the browser will close the paragraph early, leaving the rest of the caption outside the
   * element it is styled by. Spans, and spans of spans.
   */
  desc?: ReactNode;
  actions?: ReactNode;
  /**
   * The numbers this page exists to show, in the header rather than in a card.
   *
   * Optional, and most pages pass none — the row then collapses to title plus actions,
   * which is still one line shorter than it was.
   */
  stats?: readonly PageStat[];
  /**
   * ONE VIEWPORT, WITH THE TABLE AS THE SCROLLER.
   *
   * The default page grows to whatever its content needs and lets the browser scroll the
   * body. That is right for a report and wrong for a QUEUE: the parameter catalogue is 633
   * rows worked top to bottom with a modal per row, so the filter bar and the result count
   * scroll away exactly when they are needed most, and every classification costs a scroll
   * back up to re-filter.
   *
   * `fill` inverts it. The page becomes exactly the viewport, the chrome is pinned, and the
   * ONE thing that scrolls is the region the caller marks — which then owns both axes, so a
   * sticky `<thead>` and a sticky first column can finally both hold at once.
   *
   * WHY THIS IS A VARIANT AND NOT A SECOND COMPONENT: five screens want it — this one, the
   * audit trail (which asks the API for up to 500 rows), alarms, findings and telemetry —
   * and `UsersView` already demonstrated what happens when a screen hand-rolls its own page
   * shell instead: different padding from the rest of the console, a title two pixels off
   * every other title, and no access to the stats row.
   *
   * The caller is responsible for one thing, and it is the one everybody gets wrong: the
   * scrolling child needs `min-height: 0`. A flex child refuses to shrink below its content
   * height by default, so without it the child grows, the `overflow: hidden` here clips it,
   * and the rows below the fold become simply unreachable rather than scrollable.
   */
  fill?: boolean;
  /**
   * Pins the header row (title, caption, stats, actions) to the top of `main`'s scroll area
   * instead of letting it scroll away with the page.
   *
   * Opt-in and narrow on purpose — most pages here are short enough that a sticky header
   * would just be a second thing painted over content for no reason, and the header's own
   * docblock above is proud of having gotten this down to one row; a sticky variant is not
   * licence to grow it back into a fourth one. It exists for the site hub, whose Analytics
   * tab can run several screens tall and whose header carries the one control — the time
   * range — every card on that tab reads from. `--card-pin`, not `lm-glass`: the card's own opaque
   * colour, in the person's Glass look too, because the page's cards scroll beneath it
   * (`glass.css`).
   */
  stickyHeader?: boolean;
  children: ReactNode;
}): ReactNode {
  /*
   * THE PINNED HEADER PUBLISHES ITS OWN HEIGHT as `--sticky-header-h` on the page element, so a
   * second sticky bar inside the page (the site Details form's "8/34 filled · Save" bar) can pin
   * BELOW it instead of sliding over it — both used `top: 0`, and the later one won (Hamzah's
   * Details tab, 2026-09-29). Measured, not a constant: the header wraps to two lines on a
   * narrow window and its caption length varies by site.
   */
  const pageRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  /* THE MAIN TABS, Top bar layout (`app/main-tabs-host.ts`): the first `Page` on screen draws them under
     its title; the shell draws them itself only on a screen with no `Page`. */
  const [claim] = useState(nextClaimId);
  useLayoutEffect(() => {
    useMainTabsHost.getState().add(claim);
    return () => useMainTabsHost.getState().remove(claim);
  }, [claim]);
  const firstPage = useMainTabsHost((h) => h.claims[0] === claim);
  const nav = useLayoutSettings((l) => l.nav);
  const tabsHere = hasMainTabs(nav) && firstPage;
  useEffect(() => {
    const page = pageRef.current;
    const header = headerRef.current;
    if (stickyHeader !== true || !page || !header) return;
    const publish = (): void => {
      page.style.setProperty('--sticky-header-h', `${header.getBoundingClientRect().height}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(header);
    return () => ro.disconnect();
  }, [stickyHeader]);

  /* NO ENTRY FADE ON THE PAGE (2026-10-08). It used to carry `lm-slidein`, a 280 ms fade from
     transparent, so every visit to every screen spent its first third of a second looking
     disabled — measured on the Alarms board, whose numbers were already there and drawn at a
     quarter of their ink. A page arriving is not an act that needs announcing; drawers, toasts
     and popovers, which answer a click, keep the slide. `lm-page` is the hook the print rule
     reads instead (app.css). */
  return (
    <div
      ref={pageRef}
      className={`lm-page${fill === true ? ' flex flex-col' : ' mx-auto'}`}
      style={{
        maxWidth: 'var(--page-max)',
        /* No bottom pad in fill mode: 60px of air below a pinned footer is 60px stolen from
           the only thing on the page that can scroll. */
        padding: fill === true
          ? 'var(--page-pad-y) var(--page-pad-x) var(--page-pad-x)'
          : 'var(--page-pad-y) var(--page-pad-x) 60px',
        /* `100%` of `main`, NEVER `100dvh`. `main` is the viewport MINUS the header, so a
           fill page sized to the viewport overflowed it by exactly the header's height (54 px
           measured, 2026-09-29): `main` scrolled that sliver AND the page's own list scrolled —
           two scrollbars on Overview, Alarms, Findings, Telemetry and Audit. One page, one
           scroll: `main` now never has anything to scroll in fill mode. */
        ...(fill === true ? { height: '100%', overflow: 'hidden' } : {}),
      }}
    >
      {/*
        THE HEADER READS THE TYPE SCALE, and that is what turned it into a header.
        
        It used to set `text-[17px]` for the title and `text-[17px]` for the stat numbers, so
        the name of the page and the numbers on it were THE SAME SIZE. Nothing led. A screen
        where every level is within four pixels of every other level reads as uniformly small
        no matter how carefully the levels were assigned, which is the real complaint behind
        "the font is too small" — the fix is a top end to the scale, not a bigger floor.
        
        Title at --fs-xl, numbers at --fs-display, labels at --fs-micro: a 28px figure over an
        11px tracked label is a stat tile, and a 22px title above them is the page.
      */}
      <div
        ref={headerRef}
        className={`lm-page-head mb-[var(--lm-gap)] flex flex-wrap items-center gap-x-[14px] gap-y-2${stickyHeader === true ? ' lm-pin' : ''}`}
        style={{
          ...(fill === true ? { flex: 'none' } : undefined),
          /* `main` is this row's nearest scrolling ancestor in every non-fill page (see
             `Shell.tsx`'s own `<main className="... overflow-y-auto">`), so `top: 0` pins it
             to the top of the visible scroll area, not to the document. Negative margin plus
             matching padding extends the sticky surface a few pixels past the row's own
             content so a card scrolling under it does not visibly clip against the text
             baseline — the same trick `--page-pad-x` already uses on the sides. */
          ...(stickyHeader === true
            ? {
                position: 'sticky',
                /* A `fill` page scrolls inside a container that carries the page padding
                   itself, and a sticky box stops at that container's PADDING edge — so `top: 0`
                   parked the header 26px down, leaving the same empty strip `marginTop` below
                   removes on a normal page (measured: header at y=80, not 54). */
                top: fill === true ? `calc(var(--page-pad-y) * -1)` : 0,
                zIndex: 5,
                /* `--card-pin`: the card's own opaque colour in either look — the page's cards
                   scroll beneath this, so it is never see-through (`glass.css`, `.lm-pin`). */
                background: 'var(--card-pin)',
                marginInline: `calc(var(--page-pad-x) * -1)`,
                /* Up into `main`'s own top padding too: without it that padding showed as an
                   empty strip of page background above the card-coloured header (Hamzah,
                   2026-09-29, "why there's no space" / the grey band over the site name). */
                marginTop: `calc(var(--page-pad-y) * -1)`,
                paddingInline: 'var(--page-pad-x)',
                paddingTop: 'var(--page-pad-y)',
                paddingBottom: 12,
                borderBottom: '1px solid var(--border)',
              }
            : undefined),
        }}
      >
        {titleInBar === true ? (
          <>
            <h1 className="sr-only">{title}</h1>
            {lead && <div className="flex min-w-0 flex-1 items-center">{lead}</div>}
          </>
        ) : (
        /* `baseline` so the title and its caption sit on one line rather than centring
            against each other and looking misaligned. */
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px]">
          {scopeTitle === true ? (
            <ScopeTitle title={title} />
          ) : (
            <h1 className="lm-t-xl min-w-0 font-bold tracking-[-0.02em]">{title}</h1>
          )}
          {desc && (
            /* `min-w-0`, defensively: a flex item's default `min-width: auto` asks the
               browser to reserve room for its widest unbreakable run before wrapping the
               rest, and the site hub's caption is the longest `desc` in the product — three
               identity fields plus up to three figures. Without this, a narrow window can
               end up reserving more width than the caption actually needs to start
               wrapping, pushing the row wider than the viewport instead of breaking sooner. */
            <p className="lm-t-sm min-w-0" style={{ color: 'var(--muted-fg)' }}>
              {desc}
            </p>
          )}
          {lead}
        </div>
        )}

        {/*
          * `ml-auto` on the trailing cluster rather than `justify-between` on the row: with
          * `justify-between` and no stats, a lone action button is pushed to the far edge
          * while the title sits at the far left and the 900px between them is the emptiness
          * this layout is fixing. Auto-margin keeps the cluster right-aligned and lets it
          * sit next to the title when there is little of it.
          */}
        {/*
          THE STATS AND THE ACTION ARE NOT THE SAME KIND OF THING, so they do not sit at the
          same spacing.

          At `gap-x-[10px]` a 28px stat figure landed 10px from the primary button, which read
          as a four-item group with one item styled oddly rather than as "here are the numbers,
          and here is the thing you can do". A gap between the facts themselves (16 px since v2
          made them words, as the prototype's) and a hairline before the action is what separates
          reading from acting.
        */}
        {(stats !== undefined || actions !== undefined || (views !== undefined && !tabsHere)) && (
          <div className="ml-auto flex flex-wrap items-center gap-x-[16px] gap-y-2">
            {/* The page's views, at the right of the head when no tab row is drawn (sidebar layout). */}
            {!tabsHere && views}
            {/*
              * THE PAGE'S FACTS ARE WORDS (prototype v2, 2026-11-02: "facts: plain words, the value
              * in the text colour; a tone keeps its dot, never colour alone"). They were stat tiles,
              * a 28 px figure over its label, which made the page head a second dashboard above the
              * page's own. Same facts, same order, same hover text; a toned one says so with a dot
              * as well as its ink.
              */}
            {stats?.map((s) => {
              const words = (
                <>
                  {s.tone !== undefined && <span className={`lm-page-fact-dot lm-page-fact-${s.tone}`} aria-hidden />}
                  <span className="text-[var(--muted-fg)]">{s.label}</span>
                  <b className={`cl-num lm-page-fact-v${s.tone !== undefined ? ` lm-page-fact-${s.tone}` : ''}`}>{s.value}</b>
                  {s.hint && <span className="text-[var(--muted-fg)]">{s.hint}</span>}
                </>
              );
              return s.onPress !== undefined ? (
                <button key={s.label} type="button" className="lm-page-fact lm-page-fact-press" title={s.title ?? undefined} onClick={s.onPress}>
                  {words}
                </button>
              ) : (
                <span key={s.label} className="lm-page-fact" title={s.title ?? undefined}>
                  {words}
                </span>
              );
            })}
            {actions !== undefined && stats !== undefined && stats.length > 0 && (
              <span
                aria-hidden
                className="h-[26px] w-px self-center"
                style={{ background: 'var(--border)' }}
              />
            )}
            {actions}
          </div>
        )}
        {/* Row 2 of the v2 page head: the main screens at the left, the page's own views at the right. */}
        {tabsHere && (
          <div className="lm-maintabs-row">
            <MainTabsSlot />
            {views !== undefined && <div className="lm-maintabs-views">{views}</div>}
          </div>
        )}
        {band !== undefined && band !== null && <div className="min-w-0 basis-full">{band}</div>}
      </div>
      {/*
        `min-h-0` on the wrapper as well as on the caller's scroller. Both are needed: this
        one lets the wrapper shrink inside the page, and the caller's lets the scroller
        shrink inside the wrapper. Miss either and the overflow is clipped instead of
        scrolled.
      */}
      {fill === true ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * The page's title drawn as the scope picker (`Page`'s `scopeTitle`). It opens the header's own panel
 * (`HeaderScope`) under itself through the shared flag in `app/scope-picker.ts`, and registers itself
 * there so the panel knows where to stand and where focus goes back on Escape.
 */
function ScopeTitle({ title }: { title: string }): ReactNode {
  const ref = useRef<HTMLButtonElement | null>(null);
  const canPick = useScopePicker((s) => s.canPick);
  const openHere = useScopePicker((s) => s.open && s.anchor === 'title');
  const toggle = useScopePicker((s) => s.toggle);
  useLayoutEffect(() => {
    const el = ref.current;
    useScopePicker.setState({ titleEl: el });
    return () => {
      const s = useScopePicker.getState();
      if (s.titleEl !== el) return;
      /* The title leaving while its panel is open takes the panel with it: nothing is left to anchor it. */
      useScopePicker.setState({ titleEl: null, ...(s.open && s.anchor === 'title' ? { open: false } : {}) });
    };
  }, [canPick]);
  if (!canPick) return <h1 className="lm-t-xl min-w-0 font-bold tracking-[-0.02em]">{title}</h1>;
  return (
    <h1 className="lm-t-xl min-w-0 font-bold tracking-[-0.02em]">
      <button
        ref={ref}
        type="button"
        className="lm-scope-title lm-hov"
        aria-haspopup="dialog"
        aria-expanded={openHere}
        title={`${title}: choose another organisation or site`}
        onClick={() => toggle('title')}
      >
        <span className="lm-scope-title-text">{title}</span>
        <span className="flex flex-none text-[var(--muted-fg)]" aria-hidden>
          <Icon name={openHere ? 'ic-chevup' : 'ic-chevdown'} size={16} />
        </span>
      </button>
    </h1>
  );
}
